import SiteLayout from '@/components/feature/SiteLayout';
import { Breadcrumb } from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: 'ri-book-open-line',
    title: '资料先于评价',
    desc: '先把一部作品的导演、编剧、主演与分集梗概整理清楚，再谈它是否值得一看。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '尊重原创版权',
    desc: '本站不提供任何影音文件，所有内容仅为文字资料与介绍。观看请通过正规授权渠道。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '面向普通观众',
    desc: '页面结构以内容可读性为优先，避免堆砌信息，让查找与阅读都更省力。',
  },
];

const timeline = [
  { year: '2021', text: '整理第一批家庭伦理题材作品资料，建立剧集档案结构。' },
  { year: '2023', text: '扩充主创信息与分集梗概，加入年份、地区与评分维度。' },
  { year: '2025', text: '重构页面布局，新增标签关联推荐与关键词检索。' },
  { year: '2026', text: '完善移动端阅读体验，统一全站信息展示规范。' },
];

export default function AboutPage() {
  return (
    <SiteLayout>
      <div className="w-full bg-primary-950 px-4 md:px-10 pt-6 pb-10 md:pt-8 md:pb-14">
        <div className="[&_a]:text-background-400 [&_a:hover]:text-background-50 [&_span]:text-background-200 [&_i]:text-background-600">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
        </div>
        <div className="mt-5 md:mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-accent-400 mb-3">
              <span className="w-4 h-px bg-accent-500"></span>
              About Us
            </span>
            <h1 className="font-heading text-2xl md:text-[40px] leading-tight font-semibold text-background-50 mb-4">
              关于伦理片在线观看
            </h1>
            <p className="text-sm md:text-[15px] leading-8 text-background-200 max-w-2xl">
              我们是一个专注伦理题材影视剧的内容门户。不是播放平台，也不是影评社区，只是把散落各处的作品信息整理成一份更容易阅读的档案。
            </p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-3 gap-3">
            {[
              { value: '20', label: '部演示剧集' },
              { value: '180+', label: '条分集梗概' },
              { value: '5', label: '年资料积累' },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-background-50/15 bg-background-50/5 px-3 py-4">
                <p className="font-heading text-xl md:text-2xl text-accent-400">{s.value}</p>
                <p className="text-[11px] text-background-400 mt-1.5 leading-4">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
              平台定位与宗旨
            </h2>
            <div className="space-y-4 text-sm leading-8 text-foreground-700">
              <p>
                伦理题材影视的魅力，在于它总把人物放在两难的位置上：说真话会伤害家人，保持沉默又会伤害自己。我们希望通过清楚的资料整理，让观众在观看之前就能理解故事的起点，也理解每个人物为什么那样选择。
              </p>
              <p>
                因此，本站的核心工作是“归档”：剧名、年份、地区、类型标签、导演、编剧、主演、分集标题与剧情梗概。我们不设排行榜榜单式的评判，评分与热度仅作为演示数据用于展示筛选与排序功能。
              </p>
              <p>
                站内所有文字与图片均为演示内容，用于呈现页面的信息结构；站内不提供影片播放、下载或任何形式的影音文件，也不引导用户前往非授权渠道。若你是内容创作者或权利方，欢迎通过联系页面与我们沟通。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
                  <span className="w-10 h-10 rounded-lg bg-background-50 border border-background-200 text-primary-700 flex items-center justify-center mb-3">
                    <i className={`${v.icon} text-lg`}></i>
                  </span>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-1.5">{v.title}</h3>
                  <p className="text-xs leading-6 text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg overflow-hidden border border-background-200">
              <div className="w-full h-[220px] md:h-[260px] bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Quiet%20editorial%20scene%20of%20a%20warm%20dim%20study%20with%20stacks%20of%20film%20scripts%20and%20old%20photographs%20on%20a%20wooden%20desk%2C%20soft%20amber%20lamp%20light%2C%20deep%20maroon%20and%20cream%20palette%2C%20shallow%20depth%20of%20field%2C%20calm%20literary%20atmosphere&width=900&height=600&seq=ethics-about-01&orientation=landscape"
                  alt="影视资料整理工作场景"
                  title="伦理影视资料归档"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 md:p-6 bg-background-50">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">内容版权声明</h3>
                <ul className="space-y-2.5 text-xs leading-6 text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-check-line text-primary-600 mt-0.5"></i>
                    页面中的剧集名称、主创姓名与剧情文本均为本站整理的演示内容。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-primary-600 mt-0.5"></i>
                    海报与剧照为示意图，用于呈现页面视觉效果，不涉及具体作品授权。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-primary-600 mt-0.5"></i>
                    未经许可，不得将本站整理内容用于商业用途或批量转载。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-primary-600 mt-0.5"></i>
                    若发现内容有误或涉及权利问题，可通过联系页面提交说明。
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">内容建设历程</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {timeline.map((item, idx) => (
              <div key={item.year} className="relative rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="absolute top-5 right-5 text-[11px] text-foreground-300">
                  0{idx + 1}
                </span>
                <p className="font-heading text-lg text-primary-700 mb-2">{item.year}</p>
                <p className="text-xs leading-6 text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-12 md:mt-16 rounded-lg bg-primary-950 px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-lg md:text-2xl font-semibold text-background-50 mb-2">
              有合作意向或内容建议？
            </h2>
            <p className="text-xs md:text-sm leading-7 text-background-300 max-w-xl">
              无论是内容纠错、资料补充，还是版权相关沟通，都可以通过联系页面留言，我们会认真阅读每一条反馈。
            </p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-medium flex items-center gap-2 whitespace-nowrap hover:bg-accent-400 transition-colors"
          >
            前往联系页面
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}